import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ChevronDown, HelpCircle, Loader2, Send, Check, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { API } from '@/App';

/**
 * Questions & Answers.
 *
 * Starts seeded so it is useful immediately, and grows from what people actually ask — a
 * question goes to the owner, he answers it once, and the answer becomes a permanent entry.
 * Only the audiences a person belongs to are shown: a member never sees the practitioners'
 * questions, and nobody has to wade through answers meant for somebody else's job.
 */
/** Renders the **bold** runs the seeded answers use, and nothing else. */
const RichText = ({ text }) => (
  <>
    {(text || '').split(/(\*\*[^*]+\*\*)/g).map((chunk, i) =>
      chunk.startsWith('**') && chunk.endsWith('**') && chunk.length > 4
        ? <strong key={i} className="text-gray-100">{chunk.slice(2, -2)}</strong>
        : <React.Fragment key={i}>{chunk}</React.Fragment>
    )}
  </>
);

export default function QAPage() {
  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const [audiences, setAudiences] = useState([]);
  const [active, setActive] = useState(null);
  const [entries, setEntries] = useState([]);
  const [mine, setMine] = useState([]);
  const [open, setOpen] = useState({});
  const [loading, setLoading] = useState(true);
  const [question, setQuestion] = useState('');
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    try {
      const [qa, my] = await Promise.all([
        axios.get(`${API}/qa`, { headers }),
        axios.get(`${API}/qa/mine`, { headers }),
      ]);
      setAudiences(qa.data.audiences || []);
      setEntries(qa.data.entries || []);
      setMine((my.data.questions || []).filter(q => q.status === 'pending'));
      setActive(prev => prev || (qa.data.audiences?.[0]?.value ?? 'member'));
    } catch {
      toast.error('Could not load the questions');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const ask = async () => {
    if (question.trim().length < 8) return toast.error('A few more words, so it can be answered well');
    setSending(true);
    try {
      const res = await axios.post(`${API}/qa/ask`,
        { audience: active, question: question.trim() }, { headers });
      toast.success(res.data.message);
      setQuestion('');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't send that");
    } finally { setSending(false); }
  };

  const shown = entries.filter(e => e.audience === active);

  if (loading) {
    return <div className="p-12 flex justify-center"><Loader2 className="w-5 h-5 animate-spin" /></div>;
  }

  return (
    <div className="max-w-3xl space-y-8" data-testid="qa-page">
      <div>
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-white">Questions</h1>
        <p className="text-base text-gray-200 mt-3">
          The things people ask most, answered plainly. If yours isn&apos;t here, ask it at the
          bottom — it gets answered and then stays here for whoever asks next.
        </p>
      </div>

      {audiences.length > 1 && (
        <div className="flex flex-wrap gap-2" data-testid="qa-audience-tabs">
          {audiences.map(a => (
            <button
              key={a.value}
              onClick={() => setActive(a.value)}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
                active === a.value
                  ? 'bg-amber-600 text-white'
                  : 'bg-slate-900/80 text-gray-200 hover:bg-slate-800'
              }`}
              data-testid={`qa-tab-${a.value}`}
            >
              {a.label}
            </button>
          ))}
        </div>
      )}

      <div className="space-y-3" data-testid="qa-entries">
        {shown.map(e => {
          const isOpen = !!open[e.id];
          return (
            <div key={e.id} className="rounded-xl border border-white/10 overflow-hidden bg-slate-900/80 backdrop-blur-sm">
              <button
                onClick={() => setOpen(o => ({ ...o, [e.id]: !o[e.id] }))}
                className="w-full flex items-start gap-3 text-left p-4 hover:bg-white/[0.06] transition-colors"
                data-testid={`qa-question-${e.id}`}
              >
                <HelpCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-500" />
                <span className="flex-1 font-semibold text-sm text-white">{e.question}</span>
                <ChevronDown
                  className={`w-4 h-4 flex-shrink-0 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                />
              </button>
              {isOpen && (
                <div className="px-4 pb-4 pl-11 text-sm text-gray-100 whitespace-pre-line"
                     data-testid={`qa-answer-${e.id}`}>
                  <RichText text={e.answer} />
                </div>
              )}
            </div>
          );
        })}
        {!shown.length && (
          <p className="text-sm text-gray-300 py-8 text-center">
            Nothing here yet — ask the first one.
          </p>
        )}
      </div>

      {mine.length > 0 && (
        <div className="space-y-2" data-testid="qa-my-pending">
          <h2 className="text-base md:text-lg font-semibold text-white">Waiting on an answer</h2>
          {mine.map(q => (
            <div key={q.id} className="flex items-start gap-2 text-sm text-gray-100 rounded-lg
                                       border border-amber-600/40 bg-slate-900/80 backdrop-blur-sm p-3">
              <Clock className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-500" />
              <span>{q.question}</span>
            </div>
          ))}
          <p className="text-xs text-amber-300/90" data-testid="qa-pending-expectation">
            Answers usually come back within 24 hours. We&apos;ll email you and leave a
            notification here the moment each one is answered.
          </p>
        </div>
      )}

      <div className="rounded-xl border border-white/10 bg-slate-900/80 backdrop-blur-sm p-5 space-y-3" data-testid="qa-ask-box">
        <h2 className="text-base md:text-lg font-semibold text-white">Ask a question</h2>
        <p className="text-sm text-gray-200">
          Asking in <strong className="text-white">{audiences.find(a => a.value === active)?.label || 'this section'}</strong>.
          No question is too small — if you wondered it, someone else will.
        </p>
        <p className="flex items-start gap-2 text-sm text-amber-300/90" data-testid="qa-ask-expectation">
          <Clock className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-500" />
          <span>
            These are answered by a person, not a bot — expect an answer <strong>within 24 hours</strong>.
            It arrives by email and as a notification here.
          </span>
        </p>
        <textarea
          rows={3}
          className="w-full rounded-lg bg-slate-950/70 border border-white/15 p-3 text-sm text-gray-100 placeholder:text-gray-400"
          placeholder="What would you like to know?"
          value={question}
          onChange={e => setQuestion(e.target.value)}
          data-testid="qa-ask-input"
        />
        <Button onClick={ask} disabled={sending} className="gap-2" data-testid="qa-ask-submit">
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          Send it in
        </Button>
      </div>
    </div>
  );
}
